// journal/view — the Journal as periods told in prose (owner-approved
// 2026-10-01; rationale: .claude/analysis/journal-redesign-2026-10-01/proposal.md).
// A pure React projection of `JournalView`; every intent is a `JournalMsg`
// sent to the module, or an open request handed to the host.
//
// One column: the shared ViewHeader ("Journal" + the scale tabs Dagar · Månader
// · År), then the periods, newest first. Folded, a period is a heading
// and its first paragraph: the author's own text when there is any, else the
// assistant's, else an invitation to write. Opened, it lifts onto the
// elevated plane with the two voices ("Du" · "Assistenten") as text tabs and
// what lies underneath: a day's activity (the Journal rows; a resource row
// shows its own history) or a rollup's shorter summaries.

import { exhaustSwitchCase } from "@bilda/utils";
import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import type {
    AssistantText,
    JournalActivity,
    JournalScale,
} from "../../app/ports/journal.js";
import { AiInfoTooltip } from "../../components/AiInfoTooltip/AiInfoTooltip.js";
import { Button } from "../../components/Button/Button.js";
import {
    InlineLink,
    linkGlyph,
    type ResolveInlineLink,
    selectLinkTextChipView,
} from "../../components/Chat/InlineLink/InlineLink.js";
import { EmptyState } from "../../components/EmptyState/EmptyState.js";
import { Heading } from "../../components/Heading/Heading.js";
import type { IconName } from "../../components/Icon/Icon.js";
import { Icon } from "../../components/Icon/Icon.js";
import type { ResourceRef } from "../../components/Navigation/types.js";
import { Prose, type RenderProseLink } from "../../components/Prose/Prose.js";
import { Spinner } from "../../components/Spinner/Spinner.js";
import { Text } from "../../components/Text/Text.js";
import { Textarea } from "../../components/Textarea/Textarea.js";
import { type TextTab, TextTabs } from "../../components/TextTabs/TextTabs.js";
import {
    ViewColumn,
    ViewHeader,
} from "../../components/ViewHeader/ViewHeader.js";
import {
    type ActivityRow,
    EntityMiniTimeline,
    Timeline,
} from "../../components/WorkspaceHome/JournalTimeline.js";
import { ChipTextEditor } from "./ChipTextEditor.js";
import { type JournalFormat, useJournalFormat } from "./format.js";
import type { Voice } from "./model.js";
import type { JournalMsg } from "./msg.js";
import type {
    Basis,
    DetailView,
    JournalView as JournalViewModel,
    PeriodView,
} from "./view-model.js";
import { periodAnchor } from "./view-model.js";
import "./view.css";

type JournalOpen = {
    // A library resource (a chip, or an activity row's "Open").
    readonly onOpenResource: (ref: ResourceRef) => void;
    readonly onOpenConversation: (id: string) => void;
};

export type JournalViewProps = JournalOpen & {
    readonly view: JournalViewModel;
    readonly send: (msg: JournalMsg) => void;
    // The shell's clock: "today", and the instant a correction is stamped with.
    readonly nowMs: number;
    // The workspace summaries are on (else the assistant writes nothing and
    // a quiet line points to Settings).
    readonly summariesOn: boolean;
    readonly onOpenSettings: () => void;
};

// === Inline links → chips labelled by the link text ===========================

const mkRenderLink =
    (resolve: ResolveInlineLink, open: JournalOpen): RenderProseLink =>
    ({ href, children }) => (
        <InlineLink
            view={selectLinkTextChipView(
                href === undefined
                    ? { type: "plain", reason: "unsupported_href" }
                    : resolve(href),
                open,
            )}
        >
            {children}
        </InlineLink>
    );

const mkGlyphOf =
    (resolve: ResolveInlineLink) =>
    (href: string): IconName => {
        const target = resolve(href);
        switch (target.type) {
            case "resource":
                return linkGlyph(target.ref.type);
            case "conversation":
                return linkGlyph("conversation");
            case "external":
            case "plain":
                return "link";
            default:
                return exhaustSwitchCase(target);
        }
    };

// === The assistant's voice ====================================================

const AssistantMeta = ({
    assistant,
    basis,
    format,
    onCorrect,
    onUnlock,
}: {
    readonly assistant: AssistantText;
    readonly basis: Basis;
    readonly format: JournalFormat;
    // Absent (folded): the line states provenance only, no actions.
    readonly onCorrect?: () => void;
    readonly onUnlock?: () => void;
}): ReactNode => {
    const { t } = useLingui();
    const action = (label: string, onClick: (() => void) | undefined) =>
        onClick ? (
            <button type="button" className="who-openingLink" onClick={onClick}>
                {label}
            </button>
        ) : null;
    // "Correct" the verb (rätta), not the answer word (rätt).
    const correct = t({ message: "Correct", context: "fix a text" });
    const line = (text: string) => (
        <Text as="span" size="xs" tone="faint">
            {text}
        </Text>
    );
    const prose = (() => {
        if (assistant.type !== "written")
            return t`Written by AI, corrected by you.`;
        const from = (() => {
            switch (basis.type) {
                case "activity":
                    return t`Written by AI from what happened that day.`;
                case "children":
                    return t`Written by AI from the shorter summaries it covers.`;
                default:
                    return exhaustSwitchCase(basis);
            }
        })();
        return assistant.readOwnText
            ? `${from} ${t`It also read your own text.`}`
            : from;
    })();
    const body = (() => {
        switch (assistant.type) {
            case "written": {
                const when = format.when(assistant.writtenAt);
                return (
                    <>
                        {line(t`Summarized ${when}`)}
                        {action(correct, onCorrect)}
                    </>
                );
            }
            case "corrected": {
                const date = format.date(assistant.correctedAt);
                return (
                    <>
                        {line(
                            t`Corrected by you ${date}. The assistant won't rewrite it.`,
                        )}
                        {action(t`Let the assistant rewrite it`, onUnlock)}
                    </>
                );
            }
            case "unlocked": {
                const date = format.date(assistant.correctedAt);
                return (
                    <>
                        {line(
                            t`Corrected by you ${date}. The assistant may rewrite it.`,
                        )}
                        {action(correct, onCorrect)}
                    </>
                );
            }
            default:
                return exhaustSwitchCase(assistant);
        }
    })();
    return (
        <div className="who-openingMeta">
            <AiInfoTooltip prose={prose} />
            {body}
        </div>
    );
};

const AssistantVoice = ({
    period,
    assistant,
    format,
    renderLink,
    glyphOf,
    send,
    nowMs,
    heading,
}: {
    readonly period: PeriodView;
    readonly assistant: AssistantText;
    readonly format: JournalFormat;
    readonly renderLink: RenderProseLink;
    readonly glyphOf: (href: string) => IconName;
    readonly send: (msg: JournalMsg) => void;
    readonly nowMs: number;
    readonly heading: string;
}): ReactNode => {
    const { t } = useLingui();
    const { id, correction, problem } = period;
    if (correction !== null)
        return (
            <div className="jd-voice">
                <ChipTextEditor
                    initial={assistant.markdown}
                    glyphOf={glyphOf}
                    ariaLabel={t`Correct the assistant's text for ${heading}`}
                    onChange={(draft) =>
                        send({ type: "correction_drafted", draft })
                    }
                />
                <Text as="span" size="xs" tone="faint">
                    {t`Saving locks the text: the assistant won't rewrite it.`}
                </Text>
                <div className="jd-actions">
                    <Button
                        variant="primary"
                        size="sm"
                        disabled={correction.trim() === ""}
                        onClick={() =>
                            send({
                                type: "correction_saved",
                                at: new Date(nowMs).toISOString(),
                            })
                        }
                    >
                        {t`Save correction`}
                    </Button>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => send({ type: "correction_cancelled" })}
                    >
                        {t`Cancel`}
                    </Button>
                </div>
            </div>
        );
    return (
        <div className="jd-voice">
            <div className="jd-prose">
                <Prose renderLink={renderLink}>{assistant.markdown}</Prose>
            </div>
            <AssistantMeta
                assistant={assistant}
                basis={period.basis}
                format={format}
                onCorrect={() =>
                    send({ type: "correction_started", periodId: id })
                }
                onUnlock={() =>
                    send({ type: "unlock_requested", periodId: id })
                }
            />
            {assistant.type !== "written" && assistant.original !== null ? (
                <>
                    <button
                        type="button"
                        className="who-openingLink jd-quiet"
                        aria-expanded={period.showOriginal}
                        onClick={() =>
                            send({ type: "original_toggled", periodId: id })
                        }
                    >
                        {period.showOriginal
                            ? t`Hide the assistant's original`
                            : t`Show the assistant's original`}
                    </button>
                    {period.showOriginal ? (
                        <div className="jd-original">
                            <div className="who-openingMeta">
                                <AiInfoTooltip
                                    prose={t`Written by AI. Your correction replaced it.`}
                                />
                                <Text as="span" size="xs" tone="faint">
                                    {t`The assistant's original`}
                                </Text>
                            </div>
                            <div className="jd-prose">
                                <Prose renderLink={renderLink}>
                                    {assistant.original}
                                </Prose>
                            </div>
                        </div>
                    ) : null}
                </>
            ) : null}
            {problem !== null ? (
                <Text as="p" size="xs" tone="danger" className="jd-problem">
                    {problem === "unlock"
                        ? t`The text could not be unlocked.`
                        : t`Your correction could not be saved.`}
                </Text>
            ) : null}
        </div>
    );
};

// === The author's voice =======================================================

const OwnVoice = ({
    period,
    heading,
    assistantReadsOwn,
    send,
}: {
    readonly period: PeriodView;
    readonly heading: string;
    readonly assistantReadsOwn: boolean;
    readonly send: (msg: JournalMsg) => void;
}): ReactNode => {
    const { t } = useLingui();
    const { id, own } = period;
    const status = (() => {
        switch (own.status) {
            case "saved":
            case "editing":
                return (
                    <Text as="span" size="xs" tone="faint">
                        {t`Saved as you write.`}
                    </Text>
                );
            case "saving":
                return (
                    <Text as="span" size="xs" tone="faint">
                        {t`Saving…`}
                    </Text>
                );
            case "failed":
                return (
                    <span className="jd-line">
                        <Text as="span" size="xs" tone="danger">
                            {t`Your text could not be saved.`}
                        </Text>
                        <button
                            type="button"
                            className="who-openingLink"
                            onClick={() =>
                                send({ type: "own_committed", periodId: id })
                            }
                        >
                            {t`Try again`}
                        </button>
                    </span>
                );
            default:
                return exhaustSwitchCase(own.status);
        }
    })();
    return (
        <div className="jd-voice">
            <Textarea
                value={own.text}
                rows={3}
                placeholder={t`Write for yourself. What went well, what chafed, what do you want to remember?`}
                aria-label={t`Your text for ${heading}`}
                onChange={(event) =>
                    send({
                        type: "own_edited",
                        periodId: id,
                        text: event.target.value,
                    })
                }
                onBlur={() => send({ type: "own_committed", periodId: id })}
            />
            {status}
            {/* The setting itself lives once, in Settings → Assistant. */}
            <Text as="span" size="xs" tone="faint">
                {assistantReadsOwn
                    ? t`The assistant reads your text when it summarizes.`
                    : t`The assistant does not read your text.`}
            </Text>
        </div>
    );
};

// === What lies underneath =====================================================

const toActivityRow = (
    activity: JournalActivity,
    time: string,
): ActivityRow => ({
    type: activity.kind,
    id: activity.id,
    title: activity.title,
    when: time,
    whenAbsolute: time,
    at: activity.at,
    freshness: "fresh",
});

const toResourceRef = (activity: JournalActivity): ResourceRef | null => {
    const { id, title } = activity;
    switch (activity.kind) {
        case "source":
            return { type: "source", id, title };
        case "material":
            return { type: "material", id, title };
        case "package":
            return { type: "export", id, title };
        case "conversation":
            return null;
        default:
            return exhaustSwitchCase(activity.kind);
    }
};

const Detail = ({
    periodId,
    detail,
    format,
    renderLink,
    nowMs,
    send,
    open,
}: {
    readonly periodId: string;
    readonly detail: DetailView;
    readonly format: JournalFormat;
    readonly renderLink: RenderProseLink;
    readonly nowMs: number;
    readonly send: (msg: JournalMsg) => void;
    readonly open: JournalOpen;
}): ReactNode => {
    const { t } = useLingui();
    switch (detail.type) {
        case "activity": {
            if (detail.rows.length === 0) return null;
            const { selected } = detail;
            const selectedRef =
                selected === null ? null : toResourceRef(selected.row);
            return (
                <section className="jd-detail">
                    <Text
                        as="p"
                        size="xs"
                        weight="semibold"
                        tone="faint"
                        className="jd-caption"
                    >
                        {t`What happened`}
                    </Text>
                    <Timeline
                        groups={[
                            {
                                period: "today",
                                entries: detail.rows.map((r) =>
                                    toActivityRow(r, format.time(r.at)),
                                ),
                            },
                        ]}
                        dateMode="absolute"
                        caption={false}
                        selected={
                            selected === null
                                ? null
                                : toActivityRow(
                                      selected.row,
                                      format.time(selected.row.at),
                                  )
                        }
                        onSelect={(row) => {
                            const activity = detail.rows.find(
                                (r) => r.kind === row.type && r.id === row.id,
                            );
                            if (activity === undefined) return;
                            // A conversation opens where it lives; a resource
                            // first shows its own history here.
                            if (activity.kind === "conversation")
                                open.onOpenConversation(activity.id);
                            else
                                send({
                                    type: "row_selected",
                                    periodId,
                                    activity,
                                });
                        }}
                    />
                    {selected !== null && selectedRef !== null ? (
                        <div className="jd-rowDetail">
                            <EntityMiniTimeline
                                entity={toActivityRow(
                                    selected.row,
                                    format.time(selected.row.at),
                                )}
                                history={selected.history}
                                nowMs={nowMs}
                            />
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => open.onOpenResource(selectedRef)}
                            >
                                {t`Open in Created materials`}
                            </Button>
                        </div>
                    ) : null}
                </section>
            );
        }
        case "children":
            return detail.children.length === 0 ? null : (
                <ol className="jd-children">
                    {detail.children.map((child) => (
                        <li
                            key={format.childHeading(child.key)}
                            className="jd-child"
                        >
                            <button
                                type="button"
                                className="jd-childHeading"
                                onClick={() =>
                                    send({
                                        type: "child_opened",
                                        key: child.key,
                                    })
                                }
                            >
                                {format.childHeading(child.key)}
                            </button>
                            <Text as="div" size="sm" tone="dim">
                                <Prose inline renderLink={renderLink}>
                                    {child.markdown}
                                </Prose>
                            </Text>
                        </li>
                    ))}
                </ol>
            );
        default:
            return exhaustSwitchCase(detail);
    }
};

const SummarizeFailed = (): ReactNode => {
    const { t } = useLingui();
    return (
        <Text as="p" size="xs" tone="danger" className="jd-problem">
            {t`The summary could not be written.`}
        </Text>
    );
};

// "Summarize now", for a finished period the assistant has not written.
const SummarizeLine = ({
    period,
    send,
}: {
    readonly period: PeriodView;
    readonly send: (msg: JournalMsg) => void;
}): ReactNode => {
    const { t } = useLingui();
    const failed = period.problem === "summarize";
    switch (period.summarize) {
        case "none":
            return failed ? <SummarizeFailed /> : null;
        case "running":
            return (
                <Text as="span" size="xs" tone="faint">
                    {t`Summarizing…`}
                </Text>
            );
        case "offered":
            return (
                <>
                    {failed ? <SummarizeFailed /> : null}
                    <button
                        type="button"
                        className="who-openingLink jd-invite"
                        onClick={() =>
                            send({
                                type: "summarize_requested",
                                periodId: period.id,
                            })
                        }
                    >
                        <Icon name="sparkle" size="xs" />
                        {failed ? t`Try again` : t`Summarize`}
                    </button>
                </>
            );
        default:
            return exhaustSwitchCase(period.summarize);
    }
};

// === One period ===============================================================

type PeriodEnv = Omit<
    JournalViewProps,
    "view" | "summariesOn" | "onOpenSettings"
> & {
    readonly format: JournalFormat;
    readonly assistantReadsOwn: boolean;
};

const PeriodEntry = ({
    period,
    env,
}: {
    readonly period: PeriodView;
    readonly env: PeriodEnv;
}): ReactNode => {
    const { t } = useLingui();
    const { send, format } = env;
    // The period's links resolve to the targets the server listed for it.
    const renderLink = mkRenderLink(period.resolveLink, env);
    const glyphOf = mkGlyphOf(period.resolveLink);
    const { id, assistant, folded, open } = period;
    const heading = format.heading(period.key);

    const foldedBody = (): ReactNode => {
        switch (folded.type) {
            case "own":
                return (
                    <div className="jd-prose">
                        <Prose renderLink={renderLink}>{folded.markdown}</Prose>
                    </div>
                );
            case "assistant":
                return (
                    <>
                        <div className="jd-prose">
                            <Prose renderLink={renderLink}>
                                {folded.markdown}
                            </Prose>
                        </div>
                        <AssistantMeta
                            assistant={folded.assistant}
                            basis={period.basis}
                            format={format}
                        />
                    </>
                );
            case "invite":
                return (
                    <button
                        type="button"
                        className="who-openingLink jd-invite"
                        onClick={() =>
                            send({ type: "write_invited", periodId: id })
                        }
                    >
                        <Icon name="plus" size="xs" />
                        {t`Write a few lines`}
                    </button>
                );
            default:
                return exhaustSwitchCase(folded);
        }
    };

    return (
        <article
            id={periodAnchor(id)}
            className={`jd-period${open ? " jd-period--open" : ""}`}
        >
            {/* The accordion shape: the heading holds its toggle button. */}
            <Heading level={2} size="lg" className="jd-periodHeading">
                <button
                    type="button"
                    className="jd-periodToggle"
                    aria-expanded={open}
                    onClick={() =>
                        send({ type: "period_toggled", periodId: id })
                    }
                >
                    {heading}
                    <Icon
                        name={open ? "chevron-up" : "chevron-down"}
                        size="sm"
                        className="jd-chevron"
                    />
                </button>
            </Heading>
            {open ? (
                <>
                    {assistant !== null ? (
                        <TextTabs<Voice>
                            ariaLabel={t`Whose text`}
                            tabs={[
                                { value: "own", label: t`You` },
                                {
                                    value: "assistant",
                                    label: t({
                                        message: "Assistant",
                                        context: "whose text",
                                    }),
                                },
                            ]}
                            value={period.voice}
                            onChange={(voice) =>
                                send({
                                    type: "voice_chosen",
                                    periodId: id,
                                    voice,
                                })
                            }
                        />
                    ) : null}
                    {period.voice === "assistant" && assistant !== null ? (
                        <AssistantVoice
                            period={period}
                            assistant={assistant}
                            format={format}
                            renderLink={renderLink}
                            glyphOf={glyphOf}
                            send={send}
                            nowMs={env.nowMs}
                            heading={heading}
                        />
                    ) : (
                        <OwnVoice
                            period={period}
                            heading={heading}
                            assistantReadsOwn={env.assistantReadsOwn}
                            send={send}
                        />
                    )}
                    <Detail
                        periodId={id}
                        detail={period.detail}
                        format={format}
                        renderLink={renderLink}
                        nowMs={env.nowMs}
                        send={send}
                        open={env}
                    />
                </>
            ) : (
                foldedBody()
            )}
            {assistant === null ? (
                <SummarizeLine period={period} send={send} />
            ) : null}
        </article>
    );
};

// === The Journal ==============================================================

export const JournalView = (props: JournalViewProps): ReactNode => {
    const { t } = useLingui();
    const { view, send, nowMs, summariesOn, onOpenSettings } = props;
    const format = useJournalFormat(nowMs);
    const scales: readonly TextTab<JournalScale>[] = [
        { value: "day", label: t`Days` },
        { value: "month", label: t`Months` },
        { value: "year", label: t`Years` },
    ];

    const body = (): ReactNode => {
        switch (view.type) {
            case "loading":
                return (
                    <div className="jd-state">
                        <Spinner label={t`Loading the journal…`} />
                    </div>
                );
            case "failed":
                return (
                    <EmptyState
                        title={t`The journal could not be loaded`}
                        body={t`Check your connection and try again.`}
                    >
                        <Button
                            variant="secondary"
                            onClick={() => send({ type: "reload_requested" })}
                        >
                            {t`Try again`}
                        </Button>
                    </EmptyState>
                );
            case "empty":
                return (
                    <EmptyState
                        title={t`Nothing here yet`}
                        body={t`Each day you work in this workspace gets a few lines here.`}
                    />
                );
            case "loaded": {
                const env: PeriodEnv = {
                    ...props,
                    format,
                    assistantReadsOwn: view.assistantReadsOwn,
                };
                return (
                    <div className="jd-periods">
                        {view.periods.map((period) => (
                            <PeriodEntry
                                key={period.id}
                                period={period}
                                env={env}
                            />
                        ))}
                        {view.older === "none" ? null : (
                            <button
                                type="button"
                                className="who-openingLink jd-older"
                                disabled={view.older === "loading"}
                                onClick={() =>
                                    send({ type: "older_requested" })
                                }
                            >
                                {view.older === "loading"
                                    ? t`Loading older…`
                                    : t`Show older`}
                            </button>
                        )}
                        {/* Reading consent + remove-all live in Settings. */}
                        <button
                            type="button"
                            className="who-openingLink jd-quiet jd-yourTexts"
                            onClick={onOpenSettings}
                        >
                            {t`Settings for your texts`}
                        </button>
                    </div>
                );
            }
            default:
                return exhaustSwitchCase(view);
        }
    };

    return (
        <div className="who-home">
            <ViewColumn className="jd-inner">
                <ViewHeader
                    title={t`Journal`}
                    nav={
                        <TextTabs<JournalScale>
                            ariaLabel={t`Show the journal by`}
                            tabs={scales}
                            value={view.scale}
                            onChange={(scale) =>
                                send({ type: "scale_chosen", scale })
                            }
                        />
                    }
                />
                {summariesOn ? null : (
                    <span className="jd-line">
                        <Text as="span" size="xs" tone="faint">
                            {t`The assistant does not summarize your days while workspace summaries are off.`}
                        </Text>
                        <button
                            type="button"
                            className="who-openingLink"
                            onClick={onOpenSettings}
                        >
                            {t`Settings`}
                        </button>
                    </span>
                )}
                {body()}
            </ViewColumn>
        </div>
    );
};
